import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasRouteAccess, getDefaultDashboard } from '../utils/roleBasedAccess';

/**
 * ProtectedRoute Component
 * Handles authentication checks and role-based route access control
 */
const ProtectedRoute = ({ children, requiredRoles = null }) => {
  const { user, token, loading: authLoading, isAuthenticated } = useAuth();
  const [hasAccess, setHasAccess] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const checkAccess = () => {
      console.log('[ProtectedRoute] Checking access for path:', location.pathname);
      
      if (authLoading) {
        return;
      }

      if (!isAuthenticated || !user) {
        console.log('[ProtectedRoute] Not authenticated, redirecting to login');
        setHasAccess(false);
        setIsLoading(false);
        return;
      }

      const userRole = user.role;
      const currentPath = location.pathname;
      
      console.log('[ProtectedRoute] Checking role-based access:', { userRole, currentPath, requiredRoles });
      
      // Check if route requires specific roles
      if (requiredRoles && !requiredRoles.includes(userRole)) {
        console.log('[ProtectedRoute] User role not in required roles, access denied');
        setHasAccess(false);
      }
      // Check general route access using RBAC utility
      else if (!hasRouteAccess(userRole, currentPath)) {
        console.log('[ProtectedRoute] Route access denied by RBAC');
        setHasAccess(false);
      }
      else {
        console.log('[ProtectedRoute] Route access granted');
        setHasAccess(true);
      }
      
      setIsLoading(false);
    };

    checkAccess();
  }, [location.pathname, requiredRoles, user, isAuthenticated, authLoading]);

  // Show loading state while checking authentication and access
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying authentication and permissions...</p>
        </div>
      </div>
    );
  }

  // Show loading state while auth context is loading
  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying authentication and permissions...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    console.log('[ProtectedRoute] Redirecting to login, current path:', location.pathname);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to appropriate dashboard if access is denied
  if (hasAccess === false) {
    const defaultDashboard = getDefaultDashboard(user.role);
    console.log('[ProtectedRoute] Access denied, redirecting to default dashboard:', defaultDashboard);
    
    // Prevent redirect loop if we're already on the default dashboard
    if (location.pathname !== defaultDashboard) {
      return <Navigate to={defaultDashboard} replace />;
    } else {
      // If user doesn't have access to their own default dashboard, something is wrong
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">You don't have permission to access this page.</p>
            <p className="text-sm text-gray-500">Your role: {user.role}</p>
          </div>
        </div>
      );
    }
  }

  // Render protected content if authenticated and has access
  if (isAuthenticated && hasAccess !== false) {
    console.log('[ProtectedRoute] Rendering protected content');
    return children;
  }

  // Fallback loading state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
};

export default ProtectedRoute;